import {
  createRootRouteWithContext,
  Link,
  Navigate,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AlertCircle, Compass, Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { useMobileDetection } from '@/mobile/hooks/useMobileDetection';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';

interface RouterContext {
  queryClient: QueryClient;
  auth: unknown;
}

/**
 * Splash mínimo enquanto auth resolve. Evita render de versão desktop
 * antes do redirect mobile (fix bug PWA: "abre desktop primeiro").
 *
 * Cores via tokens — sem flash de tema; usa `bg-bg` que já está pintado
 * no <html> via tailwind base.
 */
function AppSplash() {
  return (
    <div
      className="min-h-dvh grid place-items-center bg-bg"
      role="status"
      aria-label="Carregando aplicação"
    >
      <Loader2 className="h-6 w-6 animate-spin text-mint-deep" strokeWidth={1.8} />
    </div>
  );
}

/**
 * RootRedirector — decide mobile vs desktop SINCRONAMENTE para evitar
 * flash da tela desktop antes do redirect.
 *
 * Antes (com bug): `useEffect + useNavigate` rodava só após primeiro paint,
 * o que renderizava `/` (Index desktop) por 1-2 segundos antes de redirecionar.
 *
 * Agora: enquanto `auth.loading=true`, mostra splash. Quando resolve,
 * se mobile + logado + na home, retorna `<Navigate>` (sem render do Outlet).
 */
function RootRedirector() {
  const isMobile = useMobileDetection();
  const { user, loading } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Bloqueia render do Outlet até auth resolver — evita pisca de tela errada
  if (loading) {
    return <AppSplash />;
  }

  // Mobile + autenticado + na home → redireciona antes do Outlet renderizar
  if (isMobile && user && pathname === '/') {
    return <Navigate to="/m" replace />;
  }

  // Desktop logado em /m: força volta pro shell desktop (caso de redimensionamento)
  if (!isMobile && user && pathname.startsWith('/m')) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function NotFoundPage() {
  return (
    <div
      className="min-h-dvh grid place-items-center p-6 bg-bg"
      style={{
        backgroundImage:
          'radial-gradient(ellipse 60% 40% at 0% 0%, rgba(199,228,215,0.35) 0%, transparent 70%)',
      }}
    >
      <Card variant="float" className="max-w-md w-full p-10 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3 bg-card-soft text-fg-muted mb-4">
          <Compass className="h-8 w-8" strokeWidth={1.6} />
        </div>
        <Eyebrow>Página não encontrada</Eyebrow>
        <h2 className="font-display text-[24px] font-bold tracking-[-0.02em] text-fg mt-2">
          Esse caminho não existe por aqui
        </h2>
        <p className="text-sm text-fg-muted mt-2 max-w-xs mx-auto">
          A URL pode estar errada ou esta seção ainda não está disponível.
        </p>
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button variant="ghost" onClick={() => window.history.back()}>
            Voltar atrás
          </Button>
          <Button variant="primary" asChild>
            <Link to="/">Ir pro início</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ErrorPage({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="min-h-dvh grid place-items-center p-6 bg-block-soft/30">
      <Card variant="float" className="max-w-lg w-full p-10 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3 bg-block-soft text-block mb-4">
          <AlertCircle className="h-8 w-8" strokeWidth={1.6} />
        </div>
        <Eyebrow className="text-block">Algo deu ruim</Eyebrow>
        <h2 className="font-display text-[24px] font-bold tracking-[-0.02em] text-fg mt-2">
          A página travou aqui no meio do caminho
        </h2>
        <p className="text-sm text-fg-muted mt-2 max-w-sm mx-auto">
          Pode ter sido um soluço de conexão. Tente carregar de novo — se persistir, fala com a
          gente.
        </p>
        {import.meta.env.DEV && (
          <pre className="text-[11px] font-mono bg-card-soft p-3 rounded-2 mt-4 text-left overflow-auto max-h-32 text-fg-muted">
            {error.message}
          </pre>
        )}
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button variant="ghost" asChild>
            <Link to="/">Voltar pro início</Link>
          </Button>
          <Button variant="primary" onClick={reset}>
            Tentar de novo
          </Button>
        </div>
      </Card>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <AuthProvider>
      <RootRedirector />
    </AuthProvider>
  ),
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});
